import { Result } from '@proton/docs-shared'
import type { DocumentUpdate } from '@proton/docs-proto'
import type { DecryptedMessage } from '@proton/docs-shared'
import { mergeUpdates } from 'yjs'
import type { LoggerInterface } from '@proton/shared/lib/logs'
import { decompressDocumentUpdate, isCompressedDocumentUpdate } from '../utils/document-update-compression'

export type UpdatePair = {
  encrypted: DocumentUpdate
  decrypted: DecryptedMessage
}

export type SquashResult = {
  unmodifiedUpdates: UpdatePair[]
  updatesAsSquashed: Uint8Array<ArrayBuffer> | undefined
}

export class SquashAlgorithm {
  constructor(private logger: LoggerInterface) {}

  async squashNormal(updates: UpdatePair[], config: { limit: number; factor: number }): Promise<Result<SquashResult>> {
    this.logger.info(
      `[Squash] Executing squash algorithm with ${updates.length} updates and config ${JSON.stringify(config)}`,
    )

    const desiredNumberOfUpdates = Math.floor(config.limit * config.factor)

    const numUpdatesToSquash = updates.length - desiredNumberOfUpdates

    if (numUpdatesToSquash <= 0) {
      this.logger.info(`[Squash] No updates to squash, returning original updates`)

      return Result.ok({
        updatesAsSquashed: undefined,
        unmodifiedUpdates: updates,
      })
    }

    this.logger.info(`[Squash] Squashing ${numUpdatesToSquash} updates`)

    const updatesToSquash = updates.slice(-numUpdatesToSquash)
    const unmodifiedUpdates = updates.slice(0, updates.length - numUpdatesToSquash)

    const updatesAsSquashed = mergeUpdates(
      updatesToSquash.map((update) => {
        const decryptedContent = update.decrypted.content
        if (isCompressedDocumentUpdate(decryptedContent)) {
          return decompressDocumentUpdate(decryptedContent)
        } else {
          return decryptedContent
        }
      }),
    ) as Uint8Array<ArrayBuffer>

    this.logger.info(`[Squash] Squashed ${updatesToSquash.length} updates`)

    return Result.ok({
      updatesAsSquashed,
      unmodifiedUpdates,
    })
  }

  squashEverything(updates: UpdatePair[]): Result<SquashResult> {
    this.logger.info(`[Squash] Squashing everything...`)

    const updatesAsSquashed = mergeUpdates(
      updates.map((update) => {
        const decryptedContent = update.decrypted.content
        if (isCompressedDocumentUpdate(decryptedContent)) {
          return decompressDocumentUpdate(decryptedContent)
        } else {
          return decryptedContent
        }
      }),
    ) as Uint8Array<ArrayBuffer>

    return Result.ok({
      updatesAsSquashed,
      unmodifiedUpdates: [],
    })
  }
}
